import { FC } from "react";
import {
  CheckIcon,
  CodeBracketIcon,
  ExclamationCircleIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
} from "@heroicons/react/24/outline";
import {
  ReleaseNoteItemDTO,
  ReleaseNoteInternalItemDTO,
} from "../../interfaces/Dtos/ReleaseNotesDTO";

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sept", "oct", "nov", "dic"];

/** «2026-10-01» → «1 oct 2026». */
export const formatFecha = (fecha: string) => {
  const [y, m, d] = fecha.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

interface Sections {
  nuevo?: ReleaseNoteItemDTO[];
  mejorado?: ReleaseNoteItemDTO[];
  corregido?: ReleaseNoteItemDTO[];
  interno?: ReleaseNoteInternalItemDTO[];
  accion?: string;
  internas?: boolean;
}

/** La única caja ámbar; va primero en cada versión. */
export const ActionBox: FC<{ text: string }> = ({ text }) => (
  <div
    className="flex gap-3 rounded-md border-l-4 border-amber-500 bg-amber-50 p-4"
    data-testid="accion"
  >
    <ExclamationCircleIcon className="h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
    <div className="text-amber-900">
      <p className="font-semibold">Qué debes hacer</p>
      <p className="whitespace-pre-line">{text}</p>
    </div>
  </div>
);

const VISIBLE = [
  { key: "nuevo" as const, title: "Nuevo", Icon: PlusIcon, color: "text-indigo-600" },
  { key: "mejorado" as const, title: "Mejorado", Icon: ArrowTrendingUpIcon, color: "text-emerald-700" },
  { key: "corregido" as const, title: "Corregido", Icon: CheckIcon, color: "text-gray-700" },
];

const ItemList: FC<{ items: ReleaseNoteItemDTO[] }> = ({ items }) => (
  <ul className="ml-7 list-disc space-y-2 text-gray-800">
    {items.map((item, i) => (
      <li key={i}>
        <span className="font-semibold">{item.area}</span>: {item.texto}
      </li>
    ))}
  </ul>
);

/**
 * Secciones de una versión o de una nota de rama, en el orden de lectura: «Qué debes hacer»
 * primero, luego Nuevo, Mejorado, Corregido y, según el ambiente, Interno o una línea apagada.
 * Solo nodos de texto: el contenido viene de archivos del repositorio, nunca como HTML.
 */
const ReleaseNoteSections: FC<{ sections: Sections; showInternal: boolean }> = ({
  sections,
  showInternal,
}) => {
  const hasVisible = VISIBLE.some((s) => sections[s.key]?.length);
  const hadInternal = showInternal ? !!sections.interno?.length : !!sections.internas;
  return (
    <div className="flex flex-col gap-5">
      {!!sections.accion && <ActionBox text={sections.accion} />}
      {VISIBLE.map(({ key, title, Icon, color }) =>
        sections[key]?.length ? (
          <section key={key} className="flex flex-col gap-2" aria-label={title}>
            <h4 className={`flex items-center gap-2 font-semibold ${color}`}>
              <Icon className="h-5 w-5" aria-hidden="true" />
              {title}
            </h4>
            <ItemList items={sections[key]!} />
          </section>
        ) : null
      )}
      {showInternal && !!sections.interno?.length && (
        <section
          className="flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50 p-4"
          aria-label="Interno"
        >
          <h4 className="flex items-center gap-2 font-semibold text-slate-600">
            <CodeBracketIcon className="h-5 w-5" aria-hidden="true" />
            Interno · sin cambios visibles
          </h4>
          <ul className="ml-7 list-disc space-y-2 text-slate-700">
            {sections.interno.map((item, i) => (
              <li key={i}>{item.texto}</li>
            ))}
          </ul>
          <p className="text-xs text-slate-500">Solo visible en desarrollo y QA.</p>
        </section>
      )}
      {!showInternal && hadInternal && (
        <p className="text-sm text-gray-500">
          {hasVisible
            ? "También incluye mejoras internas, sin cambios visibles."
            : "Esta versión trae mejoras internas, sin cambios visibles."}
        </p>
      )}
    </div>
  );
};

export default ReleaseNoteSections;
